import { AfterViewInit, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { APP_VERSION } from '../../../app-info';
import { AuthService } from '../../../services/auth.service';
import { ConnectionService } from '../../../services/connection.service';
import { LoadingService } from '../../../services/loading.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { MapService } from '../../../services/map.service';
import { RoutingService } from '../../../services/routing.service';
import { PATTERN_TEXT } from '../../../utils/normalize-string';
import { GoogleSigninComponent } from '../../googlesignin/googlesignin.component';
import { AlertComponent } from '../../shared/card/alert/alert.component';

@Component({
  selector: 'connection',
  templateUrl: './connection.component.html',
  styleUrls: ['./connection.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatSliderModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    TranslatePipe,
    AlertComponent,
    MatCheckboxModule,
    GoogleSigninComponent,
  ],
})
export class ConnectionComponent implements OnInit, OnDestroy, AfterViewInit {
  public connectionService = inject(ConnectionService);
  public loadingService = inject(LoadingService);
  public version = APP_VERSION;
  public authService = inject(AuthService);
  public routingService = inject(RoutingService);
  private route = inject(ActivatedRoute);
  public mapService = inject(MapService);
  public dialog = inject(MatDialog);
  private router = inject(Router);
  private formBuilder = inject(FormBuilder);
  private DEFAULT_PASSWORD = '*********' as const;
  private REGEX_RENAULT = RegExp('(@renault.com)');
  public isDeviceIOS = false;
  private renaultConnectSubscription: Subscription;
  public hide = true;
  public errorKey = this.connectionService.$errMsg;
  public errorMessagesDictionnary = {
    connection: 'CONNECTION.ERROR_CONNECT',
    defaultError: 'CONNECTION.ERROR_DEFAULT',
    wrongId: 'CONNECTION.ERRORID',
    idpRenault: 'CONNECTION.ERRORIDP',
  };
  public retryConnection = 0;

  get password() {
    return this.connectionForm.get('password');
  }

  get email() {
    return this.connectionForm.get('email');
  }

  get isSaveLogin() {
    return this.connectionForm.get('isSaveLogin');
  }

  public connectionForm: FormGroup = this.formBuilder.group({
    email: ['', [Validators.email]],
    password: ['', [Validators.minLength(6), Validators.maxLength(50), Validators.pattern(PATTERN_TEXT)]],
    isSaveLogin: [false],
  });

  public $isMobile = this.routingService.$isMobile;

  ngAfterViewInit() {
    this.tryAutomaticConnection();
  }

  ngOnInit() {
    this.connectionService.buttonDisabled = false;
    this.isRenault();
    this.renaultConnectSubscription = this.route.queryParamMap.subscribe(params => {
      if (params.get('code')) {
        this.connectionService.renault.code = params.get('code');
        this.connectionService.renaultValidate();
      }
    });
    if (navigator.userAgent.includes('Iphone / Ipad')) {
      this.isDeviceIOS = true;
    }

    const logConnect = LocalStorageService.getElement('accountConnect');
    if (logConnect) {
      this.email.setValue(logConnect.email);
      this.connectionService.person.user = this.email.value;
      if (logConnect.save) {
        this.connectionService.person.password = this.password.value;
        this.connectionService.person.md5Password = logConnect.password;
        this.connectionService.isLoaderReconnect = logConnect.save && !this.authService.isLoggedIn;
        this.isSaveLogin.setValue(logConnect.save);
        this.password.setValue(logConnect.password);
        this.connectionService.person.save = this.isSaveLogin.value;
      }
    }
  }

  //WEBVIEW
  connexion(us: string, p: string, save: boolean) {
    return us + p + save;
  }

  public tryAutomaticConnection() {
    if (!this.authService.isLoggedIn) {
      if (this.email.value && this.password.value && this.isSaveLogin.value) {
        this.connectionService.validate();
      } else {
        this.loadingService.disconnect();
        this.connectionService.isLoaderReconnect = false;
      }
    }
  }

  public onSubmit() {
    if (this.connectionService.isRenault) {
      const url =
        'https://idp.renault.com/nidp/oauth/nam/authz?response_type=code&client_id=36cedc50-c3a4-44da-9768-869f5acb438d&state=yN3_NKz_m-TbqF-dVbcRnaQNQSrPtX3mS3wqGv9lC2A&redirect_uri=https://' +
        window.location.host +
        '/auth/renault&scope=openid%2Barca%2Brole-ubw-irn71682';
      location.href = url;
    } else {
      this.connectionService.isLoaderReconnect = true;
      this.isRenault();
      this.connectionService.person.user = this.email.value;
      this.connectionService.person.password = this.password.value;
      this.connectionService.person.md5Password =
        this.connectionService.person.password === this.DEFAULT_PASSWORD &&
        this.connectionService.person.md5Password
          ? this.connectionService.person.md5Password
          : this.connectionService.passwordToMd5(this.connectionService.person.password);
      this.connectionService.person.save = this.isSaveLogin.value;
      if (this.connectionService.isRenault === false) {
        this.connectionService.validate();
        this.retryConnection += 1;
      }
    }
  }

  public delMdp() {
    this.password.setValue(undefined);
  }

  public isRenault() {
    this.connectionService.isRenault =
      this.REGEX_RENAULT.test(this.connectionService.person.user) ||
      this.REGEX_RENAULT.test(this.email?.value);
  }

  ngOnDestroy() {
    this.connectionService.isLoaderReconnect = false;
    this.hide = true;
    this.isDeviceIOS = !!navigator.userAgent && navigator.userAgent.includes('Iphone / Ipad');
    if (this.renaultConnectSubscription) {
      this.renaultConnectSubscription.unsubscribe();
    }
  }

  public forgetPassword() {
    this.router.navigate(['/forget-password']);
  }
}
